import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  WorkbookResult,
  WorkbookSample,
  WorkbookSheet,
} from '@erp/shared-types';
import { stableSerialize } from '@erp/shared-utils';
import { orpc } from '@/api/rpc/client';
import type { ChangeSheetResult } from './worksheetValidations';
import type { GridFocus } from './worksheetRowView';

/**
 * The Sample a Large Result's grid shows: the Worksheet's chosen one (else its
 * first rows). When it is not the one its Run took, it is taken from the
 * stored Result; choosing one saves it on the Worksheet for the next Run too.
 */
export function useWorksheetSample({
  workbookId,
  runId,
  sheet,
  result,
  readOnly,
  change,
}: {
  workbookId: string;
  /** The Run of the shown Result; absent for a Live preview. */
  runId: string | undefined;
  sheet: WorkbookSheet;
  result: WorkbookResult | undefined;
  readOnly: boolean;
  change: ChangeSheetResult;
}) {
  const [picked, pick] = useState<WorkbookSample | null>(null);
  const chosen = picked ?? sheet.sample ?? { method: 'first' };
  const taken = result?.sample?.sample;
  const same = !taken || stableSerialize(taken) === stableSerialize(chosen);
  const query = useQuery({
    ...orpc.workbooks.resultSample.queryOptions({
      input: {
        workbookId,
        runId: runId ?? '',
        sheetId: sheet.id,
        sample: chosen,
      },
    }),
    enabled: Boolean(taken && runId && !same),
  });
  const focus: GridFocus | undefined =
    !same && query.data
      ? { rows: query.data.rows, rowNumbers: query.data.rowNumbers }
      : undefined;
  return {
    /** Whether the shown Result is a Large one's Sample at all. */
    sampled: Boolean(taken),
    chosen,
    focus,
    pending: !same && query.isFetching,
    error: query.error?.message ?? null,
    /** Another Sample can be taken only from a completed Run's Result. */
    switchable: Boolean(runId),
    choose(sample: WorkbookSample) {
      pick(sample);
      if (!readOnly) change((latest) => ({ ...latest, sample }));
    },
  };
}
